import moment from "moment";
import config from "config/config";
import { getTypeOfPolicy, isFlagEnabled, isPolicyInGrace } from "js/utilities";
import { getToken } from "js/formatting/getToken";
import { convertToTitleCase, getName } from "js/formatting";

export async function getPolicies(policyIds) {
  try {
    // Fetch policies from the API
    const response = await fetchPolicies(policyIds);
    if (!response.ok) {
      return handleErrorResponse(response);
    }

    const data = await response.json();
    const allPolicies = [];
    const allClaims = [];
    const filteredPolicies = initializeFilteredPolicies();

    // Process response data
    data.forEach(
      processResponsePolicy(allPolicies, allClaims, filteredPolicies)
    );

    return {
      agentPolicies: allPolicies,
      agentClaims: allClaims,
      filteredPolicies: filteredPolicies,
      isAgentLoading: false,
      errorOnAgent: "",
      agenStatus: null,
    };
  } catch (error) {
    return handleCatchError(error);
  }
}

// Fetch policies from the server
async function fetchPolicies(policyIds) {
  const options = createFetchOptions(policyIds);
  return fetch(config.REG_API_BASE_URL + "/User/policydetail", options);
}

// Create fetch options with headers
function createFetchOptions(policyIds) {
  return {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `bearer ${getToken()}`,
    },
    body: JSON.stringify({ contractIds: policyIds }),
  };
}

// Initialize filtered policies object
function initializeFilteredPolicies() {
  return {
    inForcePolicies: [],
    inGracePolicies: [],
    inactivePolicies: [],
    reinstatedPolicies: [],
    deathClaimPolicies: [],
    lapsedPolicies: [],
    conditionallyIssuedPolicies: [],
    declinedPolicies: [],
  };
}

// Handle API error response
function handleErrorResponse(response) {
  return response.status === 401
    ? response.status
    : {
        agentPolicies: [],
        agentClaims: [],
        filteredPolicies: initializeFilteredPolicies(),
        isAgentLoading: false,
        errorOnAgent: "",
        agenStatus: null,
      };
}

// Handle errors in the try-catch block
function handleCatchError(error) {
  return error.status === 401
    ? error.status
    : {
        agentPolicies: [],
        agentClaims: [],
        filteredPolicies: [],
        agenStatus: error?.message,
      };
}

// Process each policy record from response data
function processResponsePolicy(allPolicies, allClaims, filteredPolicies) {
  return (resp) => {
    const policyDetails = JSON.parse(resp.policyDetails);
    if (policyDetails) {
      const policies = policyDetails.gXdpApi.gIdpPayLoad.policyHolder.policies;
      const ownerFullName = convertToTitleCase(
        `${resp?.ownerFirstName} ${resp?.ownerLastName}`
      );

      policies.forEach((policy) => {
        if (!isPolicyExist(allPolicies, policy)) {
          categorizePolicy(
            policy,
            ownerFullName,
            allPolicies,
            allClaims,
            filteredPolicies
          );
        }
      });
    }
  };
}

// Check if a policy already exists
function isPolicyExist(allPolicies, policy) {
  return allPolicies.find((p) => p.oldPolicy === policy.oldPolicy);
}

// Categorize a policy into the appropriate filteredPolicies category
function categorizePolicy(
  policy,
  ownerFullName,
  allPolicies,
  allClaims,
  filteredPolicies
) {
  if (isPolicyInGrace(policy)) {
    filteredPolicies.inGracePolicies.push(policy);
    addToClaims(policy, allClaims);
    allPolicies.push({
      ownerFullName,
      insuredFullName: getInsuredFullName(policy),
      ...policy,
    });
    return;
  }

  if (isAnnuityPolicyWithoutFlag(policy)) return;

  switch (policy?.status?.toLowerCase()) {
    case "active":
    case "extended term":
    case "reducedpaidup":
    case "paid-up":
      addPolicyToFiltered(
        "inForcePolicies",
        policy,
        ownerFullName,
        allPolicies,
        allClaims,
        filteredPolicies
      );
      break;

    case "reinstated":
      addPolicyToFiltered(
        "reinstatedPolicies",
        policy,
        ownerFullName,
        allPolicies,
        allClaims,
        filteredPolicies
      );
      break;
    case "inactive":
    case "terminated":
    case "cancelled":
    case "surrendered":
    case "matured":
    case "expired":
    case "deceased":
    case "term conversion":
    case "administrative rewrite":
      addInactivePolicies(
        policy,
        ownerFullName,
        allPolicies,
        allClaims,
        filteredPolicies
      );
      break;

    case "lapsed":
      addLapsedPolicies(
        policy,
        ownerFullName,
        allPolicies,
        allClaims,
        filteredPolicies
      );
      break;

    case "conditionally issued":
      addPolicyToFiltered(
        "conditionallyIssuedPolicies",
        policy,
        ownerFullName,
        allPolicies,
        allClaims,
        filteredPolicies
      );
      break;

    case "declined":
      addPolicyToFiltered(
        "declinedPolicies",
        policy,
        ownerFullName,
        allPolicies,
        allClaims,
        filteredPolicies
      );
      break;

    default:
      break;
  }
}

// Check if the policy is an annuity and the corresponding flag is not enabled
function isAnnuityPolicyWithoutFlag(policy) {
  return (
    getTypeOfPolicy(policy.product.type) === "annuity" &&
    !isFlagEnabled("ENABLE_ANNUITIES")
  );
}

function getInsuredFullName(policy) {
  const insuredParty = policy?.parties?.find(
    (p) => p?.person?.type === "Insured"
  );
  if (insuredParty?.person) return getName(insuredParty?.person, "titlecase");
}
// Add policy to filtered policies and claims
function addPolicyToFiltered(
  category,
  policy,
  ownerFullName,
  allPolicies,
  allClaims,
  filteredPolicies
) {
  allPolicies.push({
    ownerFullName,
    insuredFullName: getInsuredFullName(policy),
    ...policy,
  });
  addToClaims(policy, allClaims);
  filteredPolicies[category].push(policy);
}

// Add inactive policies based on their statusDate
function addInactivePolicies(
  policy,
  ownerFullName,
  allPolicies,
  allClaims,
  filteredPolicies
) {
  if (policy.statusDate) {
    const duration = moment().diff(moment(policy?.statusDate), "years", true);
    if (duration <= 2) {
      addPolicyToFiltered(
        "inactivePolicies",
        policy,
        ownerFullName,
        allPolicies,
        allClaims,
        filteredPolicies
      );
    }
  }
}

// Add lapsed policies based on their statusDate
function addLapsedPolicies(
  policy,
  ownerFullName,
  allPolicies,
  allClaims,
  filteredPolicies
) {
  if (policy.statusDate) {
    const duration = moment().diff(moment(policy?.statusDate), "years", true);
    if (duration <= 2) {
      addPolicyToFiltered(
        "lapsedPolicies",
        policy,
        ownerFullName,
        allPolicies,
        allClaims,
        filteredPolicies
      );
    }
  }
}

// Add death claims if applicable
function addToClaims(policy, allClaims) {
  const claimStatus = policy?.deathClaim?.deathClaimStatus;
  if (["01", "1", "92", "93", "97", "98", "99"].includes(claimStatus)) {
    allClaims.push({
      ...policy.deathClaim,
      claimStatus,
      productType: policy.product.type,
      contractID: policy.contractID,
      oldPolicy: policy.oldPolicy,
      claimNumber: policy.deathClaim.deathClaimNumber,
    });
  }
}
